import React, {useState, useEffect} from 'react';
import {
  Modal,
  Box,
  Button,
  IconButton,
  Card,
  CardMedia,
  Typography,
  Fade,
} from '@mui/material';

import {
  useGetCampaignsByUserQuery,
  useHideCampaignForUserMutation,
} from 'store/slices/campaignSlice';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import {cancelButton, warningModalButton} from 'styles/MUIStyles/buttons';
import styles from './CampaignModal.module.scss';
import {useGetProfileDataQuery} from 'store/slices/profile';
import {useAppSelector} from 'store/store';
import {UserRole} from 'enums';

export const CampaignModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const role = useAppSelector((state) => state.auth.role);

  const {data: user, isLoading: userLoading} = useGetProfileDataQuery();
  const {data: campaigns = [], isLoading: campaignsLoading} =
    useGetCampaignsByUserQuery({userId: user?.id!}, {skip: !user?.id});

  const [hideForUser, {isLoading: hideLoading}] =
    useHideCampaignForUserMutation();

  useEffect(() => {
    if (!campaignsLoading && campaigns.length > 0) {
      setIsOpen(true);
    }
  }, [campaignsLoading, campaigns.length]);

  if (userLoading || campaignsLoading) return null;
  if (campaigns.length === 0) return null;

  const handleNext = (e: any) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1 < campaigns.length ? prev + 1 : prev));
  };

  const handlePrev = (e: any) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const confirmHide = async () => {
    const id = campaigns[currentIndex].id;
    await hideForUser({campaignId: id, userId: user?.id!}).unwrap();
    setConfirmOpen(false);
    setIsOpen(false);
  };

  const renderSlide = () => {
    const item = campaigns[currentIndex];
    if (!item) return null;

    return (
      <Fade in timeout={350}>
        <Card
          sx={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
            position: 'relative',
          }}
        >
          <CardMedia
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('.arrow-btn')) return;
              window.open(item.link, '_blank');
            }}
            component="img"
            src={item.imageUrl || '/hero.jpg'}
            alt={item.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              userSelect: 'none',
            }}
          />

          {/* LEFT ARROW */}
          <IconButton
            className="arrow-btn"
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.75)',
              boxShadow: 2,
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          {/* RIGHT ARROW */}
          <IconButton
            className="arrow-btn"
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.75)',
              boxShadow: 2,
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Card>
      </Fade>
    );
  };

  const renderDots = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 1.2,
        mt: 1.5,
      }}
    >
      {campaigns.map((_, idx) => (
        <Box
          key={idx}
          onClick={() => setCurrentIndex(idx)}
          sx={{
            width: currentIndex === idx ? 12 : 8,
            height: currentIndex === idx ? 12 : 8,
            borderRadius: '50%',
            cursor: 'pointer',
            transition: '0.2s',
            backgroundColor: currentIndex === idx ? 'var(--primary)' : 'var(--border)',
          }}
        />
      ))}
    </Box>
  );
  if (role === UserRole.Admin || role === UserRole.Dealer) return null;
  return (
    <>
      {/* ================= FULLSCREEN MODAL ================= */}
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <Box
          sx={{
            position: 'fixed',
            inset: '20px',
            width: 'calc(100vw - 40px)',
            height: 'calc(100vh - 40px)',
            backgroundColor: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              width: '100%',
              padding: '20px 30px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--bg-subtle)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <Typography variant="h6" fontWeight={700} color="var(--text-primary)">
              {campaigns[currentIndex]?.name}
            </Typography>

            <div className={styles.closeIcon} onClick={() => setIsOpen(false)}>
              <CloseIcon />
            </div>
          </Box>

          {/* SLIDE AREA */}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {renderSlide()}
          </Box>

          {/* DOTS */}
          <Box
            sx={{
              py: 1,
              backgroundColor: 'var(--bg-surface)',
              borderTop: '1px solid var(--border)',
            }}
          >
            {renderDots()}
          </Box>

          {/* FOOTER BUTTONS */}
          <Box
            sx={{
              width: '100%',
              padding: '15px 30px',
              display: 'flex',
              justifyContent: 'space-between',
              backgroundColor: 'var(--bg-subtle)',
              borderTop: '1px solid var(--border)',
            }}
          >
            <Box sx={{display: 'flex', gap: 2}}>
              <Button
                sx={{
                  ...warningModalButton,
                  backgroundColor: 'var(--accent)',
                  '&:hover': {backgroundColor: 'var(--primary-hover)'},
                }}
                onClick={() => setIsOpen(false)}
              >
                Close
              </Button>

              <Button
                sx={{
                  ...warningModalButton,
                  backgroundColor: 'var(--accent)',
                  '&:hover': {backgroundColor: 'var(--primary-hover)'},
                }}
                disabled={hideLoading}
                onClick={() => setConfirmOpen(true)}
              >
                Don't show again
              </Button>
            </Box>

            <Button
              sx={{
                ...warningModalButton,
                backgroundColor: 'var(--accent)',
                '&:hover': {backgroundColor: 'var(--primary-hover)'},
              }}
              onClick={() =>
                window.open(campaigns[currentIndex].link, '_blank')
              }
            >
              Open
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ================= CONFIRM MODAL ================= */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 400,
            p: '25px 30px',
            borderRadius: 4,
            backgroundColor: 'var(--bg-surface)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Are you sure?
          </Typography>

          <Typography mb={3}>
            This campaign will no longer be shown to you.
          </Typography>

          <Box sx={{display: 'flex', justifyContent: 'center', gap: 2}}>
            <Button sx={cancelButton} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>

            <Button
              sx={{
                ...warningModalButton,
                backgroundColor: 'var(--danger)',
                '&:hover': {backgroundColor: 'var(--danger)'},
              }}
              disabled={hideLoading}
              onClick={confirmHide}
            >
              Yes, hide
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
